import React, { useState, useEffect } from 'react';
import { Lock, Save, Plus, Trash2, MapPin, TrendingUp, DollarSign, Calendar, BarChart3, CheckCircle, AlertCircle, Users, Eye, X, FileText, Phone, Mail, Globe, Clock, Check, XCircle, ChevronDown, ChevronUp, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings' or 'vendors'
  
  // Booking stats state
  const [bookingData, setBookingData] = useState({
    total: 0,
    today: 0,
    transactionVolume: 0,
    cities: []
  });
  
  // Vendor applications state
  const [applications, setApplications] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expandedApp, setExpandedApp] = useState(null);
  const [loadingApps, setLoadingApps] = useState(false);

  // Fetch data on load
  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchApplications();
    }
  }, [isAuthenticated]);
  
  useEffect(() => {
    if (isAuthenticated) {
      fetchApplications();
    }
  }, [statusFilter, isAuthenticated]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/booking-stats`);
      if (response.ok) {
        const data = await response.json();
        setBookingData(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  const fetchApplications = async () => {
    setLoadingApps(true);
    try {
      const response = await fetch(`${API_URL}/api/vendor-applications?status=${statusFilter}`, {
        headers: { 'X-Admin-Password': password }
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoadingApps(false);
    }
  };
  
  const handleApprove = async (vendorId, vendorName) => {
    try {
      const response = await fetch(`${API_URL}/api/vendor-approve/${vendorId}`, {
        method: 'POST',
        headers: { 'X-Admin-Password': password }
      });
      
      if (response.ok) {
        toast.success(`${vendorName} has been approved!`);
        fetchApplications();
      } else {
        toast.error('Failed to approve vendor');
      }
    } catch (error) {
      console.error('Error approving vendor:', error);
      toast.error('Error approving vendor');
    }
  };
  
  const handleReject = async (vendorId, vendorName) => {
    const reason = prompt('Enter rejection reason (optional):');
    try {
      const response = await fetch(`${API_URL}/api/vendor-reject/${vendorId}?reason=${encodeURIComponent(reason || '')}`, {
        method: 'POST',
        headers: { 'X-Admin-Password': password }
      });
      
      if (response.ok) {
        toast.success(`${vendorName} has been rejected`);
        fetchApplications();
      } else {
        toast.error('Failed to reject vendor');
      }
    } catch (error) {
      console.error('Error rejecting vendor:', error);
      toast.error('Error rejecting vendor');
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'bHyyve2026') {
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      setAuthError('Invalid password');
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setSaveStatus(null);
    
    try {
      const response = await fetch(`${API_URL}/api/booking-stats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: password,
          data: bookingData
        }),
      });
      
      if (response.ok) {
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving stats:', error);
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: parseInt(value) || 0
    }));
  };

  const addCity = () => {
    setBookingData(prev => ({
      ...prev,
      cities: [...prev.cities, { name: '', count: 0, trend: '+100%' }]
    }));
  };

  const updateCity = (index, field, value) => {
    setBookingData(prev => ({
      ...prev,
      cities: prev.cities.map((city, i) => 
        i === index 
          ? { ...city, [field]: field === 'count' ? (parseInt(value) || 0) : value }
          : city
      )
    }));
  };

  const removeCity = (index) => {
    setBookingData(prev => ({
      ...prev,
      cities: prev.cities.filter((_, i) => i !== index)
    }));
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center px-4">
        <div className="bg-gray-900 border-2 border-yellow-400/30 rounded-2xl p-8 w-full max-w-md">
          <div className="flex justify-center mb-6">
            <div className="bg-yellow-400 p-4 rounded-full">
              <Lock className="text-black" size={32} />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white text-center mb-2">Admin Dashboard</h1>
          <p className="text-gray-400 text-center mb-8">Enter password to access booking management</p>
          
          <form onSubmit={handleLogin}>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 mb-4"
            />
            {authError && (
              <p className="text-red-400 text-sm mb-4 flex items-center gap-2">
                <AlertCircle size={16} />
                {authError}
              </p>
            )}
            <Button
              type="submit"
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3"
            >
              Access Dashboard
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black py-8 px-4">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="text-yellow-400" />
              Admin Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Manage bookings and vendor applications</p>
          </div>
          <a 
            href="/gift-admin" 
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold rounded-xl hover:from-yellow-500 hover:to-yellow-600 transition-all"
          >
            <Gift size={18} />
            Gift Admin
          </a>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all ${
              activeTab === 'bookings' 
                ? 'bg-yellow-400 text-black' 
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Calendar size={18} />
            Booking Stats
          </button>
          <button
            onClick={() => setActiveTab('vendors')}
            className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all ${
              activeTab === 'vendors' 
                ? 'bg-yellow-400 text-black' 
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Users size={18} />
            Vendor Approvals
            {applications.filter(a => a.status === 'pending').length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {applications.filter(a => a.status === 'pending').length}
              </span>
            )}
          </button>
        </div>

        {/* Booking Stats Tab */}
        {activeTab === 'bookings' && (
          <>
            {/* Save Button */}
            <div className="flex justify-end mb-4">
              <Button
                onClick={handleSave}
                disabled={isLoading}
                className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-6 py-3 flex items-center gap-2"
              >
                {isLoading ? (
                  <>Saving...</>
                ) : saveStatus === 'success' ? (
                  <>
                    <CheckCircle size={18} />
                    Saved!
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </Button>
            </div>

            {/* Status Message */}
            {saveStatus === 'success' && (
              <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
                <CheckCircle size={18} />
                Changes saved successfully! The website will reflect these updates.
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
                <AlertCircle size={18} />
                Failed to save changes. Please try again.
              </div>
            )}

            {/* Overall Stats */}
            <div className="bg-gray-900 border-2 border-gray-800 rounded-2xl p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <TrendingUp className="text-yellow-400" />
            Overall Statistics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2">
                <Calendar size={14} />
                Total Bookings (Province-Wide)
              </label>
              <Input
                type="number"
                value={bookingData.total}
                onChange={(e) => updateField('total', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white text-2xl font-bold"
              />
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2">
                <TrendingUp size={14} />
                Today's Bookings
              </label>
              <Input
                type="number"
                value={bookingData.today}
                onChange={(e) => updateField('today', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white text-2xl font-bold"
              />
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2">
                <DollarSign size={14} />
                Transaction Volume ($)
              </label>
              <Input
                type="number"
                value={bookingData.transactionVolume}
                onChange={(e) => updateField('transactionVolume', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white text-2xl font-bold"
              />
            </div>
          </div>
        </div>

        {/* City Stats */}
        <div className="bg-gray-900 border-2 border-gray-800 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <MapPin className="text-yellow-400" />
              City Breakdown
            </h2>
            <Button
              onClick={addCity}
              className="bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-400 border border-yellow-400/50 font-semibold px-4 py-2 flex items-center gap-2"
            >
              <Plus size={18} />
              Add City
            </Button>
          </div>

          {bookingData.cities.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MapPin size={48} className="mx-auto mb-4 opacity-50" />
              <p>No cities added yet. Click "Add City" to start.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bookingData.cities.map((city, index) => (
                <div
                  key={index}
                  className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-4"
                >
                  <div className="flex-1">
                    <label className="block text-gray-500 text-xs mb-1">City Name</label>
                    <Input
                      type="text"
                      value={city.name}
                      onChange={(e) => updateCity(index, 'name', e.target.value)}
                      placeholder="e.g., Toronto"
                      className="bg-gray-900 border-gray-600 text-white"
                    />
                  </div>
                  
                  <div className="w-full md:w-32">
                    <label className="block text-gray-500 text-xs mb-1">Bookings</label>
                    <Input
                      type="number"
                      value={city.count}
                      onChange={(e) => updateCity(index, 'count', e.target.value)}
                      className="bg-gray-900 border-gray-600 text-white"
                    />
                  </div>
                  
                  <div className="w-full md:w-32">
                    <label className="block text-gray-500 text-xs mb-1">Trend</label>
                    <Input
                      type="text"
                      value={city.trend}
                      onChange={(e) => updateCity(index, 'trend', e.target.value)}
                      placeholder="+100%"
                      className="bg-gray-900 border-gray-600 text-white"
                    />
                  </div>
                  
                  <button
                    onClick={() => removeCity(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10 p-2 rounded-lg transition-colors self-end md:self-center"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="mt-8 bg-gray-900 border-2 border-yellow-400/30 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">Preview</h2>
          <div className="bg-black rounded-xl p-6">
            <div className="flex flex-wrap justify-center gap-6 text-center">
              <div>
                <p className="text-gray-400 text-sm">Total Province-Wide</p>
                <p className="text-3xl font-bold text-yellow-400">{bookingData.total.toLocaleString()}+</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Today's Bookings</p>
                <p className="text-3xl font-bold text-white">{bookingData.today}+</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Transaction Volume</p>
                <p className="text-3xl font-bold text-green-400">${bookingData.transactionVolume.toLocaleString()}</p>
              </div>
            </div>
            
            {bookingData.cities.length > 0 && (
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                {bookingData.cities.map((city, index) => (
                  <div key={index} className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-center">
                    <p className="text-white font-semibold">{city.name || 'City Name'}</p>
                    <p className="text-yellow-400 text-xl font-bold">{city.count}</p>
                    <p className="text-green-400 text-sm">{city.trend}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
          </>
        )}

        {/* Vendor Approvals Tab */}
        {activeTab === 'vendors' && (
          <>
            {/* Status Filter */}
            <div className="flex gap-2 mb-6">
              {['pending', 'approved', 'rejected', 'all'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    statusFilter === status
                      ? status === 'pending' ? 'bg-yellow-400 text-black'
                        : status === 'approved' ? 'bg-green-500 text-white'
                        : status === 'rejected' ? 'bg-red-500 text-white'
                        : 'bg-gray-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
              <button
                onClick={fetchApplications}
                className="ml-auto px-4 py-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700"
              >
                Refresh
              </button>
            </div>

            {/* Applications List */}
            {loadingApps ? (
              <div className="text-center py-12 text-gray-400">
                Loading applications...
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12 bg-gray-900 rounded-2xl border-2 border-gray-800">
                <Users size={48} className="mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400">No {statusFilter !== 'all' ? statusFilter : ''} applications found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <div
                    key={app.id}
                    className="bg-gray-900 border-2 border-gray-800 rounded-2xl overflow-hidden"
                  >
                    {/* Application Header */}
                    <div
                      className="p-5 cursor-pointer hover:bg-gray-800/50 transition-colors"
                      onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-yellow-400/20 rounded-full flex items-center justify-center">
                            <Users className="text-yellow-400" size={24} />
                          </div>
                          <div>
                            <h3 className="text-white font-bold text-lg">{app.name}</h3>
                            <div className="flex items-center gap-4 text-gray-400 text-sm mt-1">
                              <span className="flex items-center gap-1">
                                <Phone size={14} />
                                {app.phone}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail size={14} />
                                {app.email}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            app.status === 'pending' ? 'bg-yellow-400/20 text-yellow-400'
                              : app.status === 'approved' ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                          </span>
                          {expandedApp === app.id ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedApp === app.id && (
                      <div className="border-t border-gray-800 p-5 bg-gray-800/30">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Contact Info */}
                          <div>
                            <h4 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
                              <Users size={16} />
                              Contact Information
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p className="text-gray-400">Name: <span className="text-white">{app.name}</span></p>
                              <p className="text-gray-400">Phone: <span className="text-white">{app.phone}</span></p>
                              <p className="text-gray-400">Email: <span className="text-white">{app.email}</span></p>
                              {app.website && (
                                <p className="text-gray-400">Website: <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:underline">{app.website}</a></p>
                              )}
                            </div>
                          </div>

                          {/* Application Info */}
                          <div>
                            <h4 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
                              <FileText size={16} />
                              Application Details
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p className="text-gray-400">Application ID: <span className="text-white font-mono text-xs">{app.id}</span></p>
                              <p className="text-gray-400">Submitted: <span className="text-white">{new Date(app.timestamp).toLocaleDateString()}</span></p>
                              {app.promos && (
                                <p className="text-gray-400">Promotions: <span className="text-white">{app.promos}</span></p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Business & Payment Details - New Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                          {/* Business Details */}
                          <div>
                            <h4 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
                              <Globe size={16} />
                              Business Details
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p className="text-gray-400">HST Number: <span className="text-white">{app.hstNumber || 'Not provided'}</span></p>
                              <p className="text-gray-400">Service Cities: <span className="text-white">
                                {app.serviceCities && app.serviceCities.length > 0 
                                  ? app.serviceCities.join(', ') 
                                  : 'Not specified'}
                              </span></p>
                            </div>
                          </div>

                          {/* Payment/Remuneration Details */}
                          <div>
                            <h4 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
                              <DollarSign size={16} />
                              Remuneration Method
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p className="text-gray-400">Method: <span className="text-white">
                                {app.paymentMethod === 'interac' ? 'Interac e-Transfer' 
                                  : app.paymentMethod === 'direct_deposit' ? 'Direct Deposit' 
                                  : 'Not specified'}
                              </span></p>
                              {app.paymentMethod === 'interac' && app.remunerationDetails?.interacEmail && (
                                <p className="text-gray-400">Interac Email: <span className="text-white">{app.remunerationDetails.interacEmail}</span></p>
                              )}
                              {app.paymentMethod === 'direct_deposit' && app.remunerationDetails && (
                                <>
                                  <p className="text-gray-400">Transit #: <span className="text-white">{app.remunerationDetails.transitNumber || 'N/A'}</span></p>
                                  <p className="text-gray-400">Institution #: <span className="text-white">{app.remunerationDetails.institutionNumber || 'N/A'}</span></p>
                                  <p className="text-gray-400">Account #: <span className="text-white">{app.remunerationDetails.accountNumber || 'N/A'}</span></p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Uploaded Files */}
                        {app.files && Object.keys(app.files).length > 0 && (
                          <div className="mt-6">
                            <h4 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
                              <FileText size={16} />
                              Uploaded Documents
                            </h4>
                            <div className="flex flex-wrap gap-3">
                              {Object.entries(app.files).map(([key, fileData]) => {
                                // Handle both old format (string) and new format (object)
                                const filename = typeof fileData === 'string' ? fileData : fileData?.saved_name;
                                const displayName = typeof fileData === 'string' ? key : fileData?.original_name || key;
                                if (!filename) return null;
                                return (
                                  <a
                                    key={key}
                                    href={`${API_URL}/api/uploads/${app.id}/${filename}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm text-white transition-colors"
                                  >
                                    <FileText size={16} className="text-yellow-400" />
                                    {displayName}
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        {app.status === 'pending' && (
                          <div className="mt-6 flex gap-3">
                            <Button
                              onClick={() => handleApprove(app.id, app.name)}
                              className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 flex items-center gap-2"
                            >
                              <Check size={18} />
                              Approve Vendor
                            </Button>
                            <Button
                              onClick={() => handleReject(app.id, app.name)}
                              className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 flex items-center gap-2"
                            >
                              <XCircle size={18} />
                              Reject
                            </Button>
                          </div>
                        )}

                        {app.status === 'approved' && app.approved_at && (
                          <div className="mt-4 text-sm text-green-400 flex items-center gap-2">
                            <CheckCircle size={16} />
                            Approved on {new Date(app.approved_at).toLocaleDateString()}
                          </div>
                        )}

                        {app.status === 'rejected' && (
                          <div className="mt-4 text-sm text-red-400">
                            <p className="flex items-center gap-2">
                              <XCircle size={16} />
                              Rejected on {app.rejected_at ? new Date(app.rejected_at).toLocaleDateString() : 'N/A'}
                            </p>
                            {app.rejection_reason && (
                              <p className="mt-1 text-gray-400">Reason: {app.rejection_reason}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Back Link */}
        <div className="mt-8 text-center">
          <a href="/" className="text-yellow-400 hover:text-yellow-300 transition-colors">
            ← Back to Website
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
